import type { PredictionNavNode } from '@/services/prediction/types'

/**
 * Sort by `ordering` ascending. BE returns nodes in this order today, but
 * we re-sort defensively so consumers stay correct if BE ever drops the
 * implicit ordering guarantee.
 *
 * (ported from cdc-web src/components/knockOuts/NavTree/utils.ts)
 */
export function sortByOrdering(nodes: PredictionNavNode[]): PredictionNavNode[] {
  return [...nodes].sort((a, b) => a.ordering - b.ordering)
}

/**
 * Active state from the locally selected node's path: a node is active when
 * its path is the selected path or an ancestor of it. Used by folder rows to
 * auto-expand when the selection sits somewhere inside their subtree.
 *
 * cdc-web compares against the URL pathname; the cdc-market port has no URL
 * sync, so `selectedPath` is fed from the section's local selection state.
 */
export function isOnActivePath(node: PredictionNavNode, selectedPath: string): boolean {
  if (!node.path) return false
  if (node.path === selectedPath) return true
  return selectedPath.startsWith(`${node.path}/`)
}

/**
 * BE represents the catch-all "All" tab as a destination with `slug === 'all'`.
 * This is the one place we read that BE convention by name — PRD §6.1.2 AC 7
 * specifically excludes "All" from showing an event count, and BE's slug is
 * the only stable signal we have for telling that node apart from sibling
 * destinations. If BE ever renames the concept this single check moves with it.
 */
export const ALL_NODE_SLUG = 'all'

/** Display name BE uses for the catch-all "All" entry. */
const ALL_NODE_DISPLAY_NAME = 'All'

/**
 * True when `node` is the catch-all "All" entry. BE marks it by `slug === 'all'`
 * (the original convention) or by `display_name` — either the bare "All" or the
 * affixed "All <Folder>" form (OG-7173 feedback, Caleb 6/9: "All Soccer", "All
 * Politics"…). We own only the display; the affix itself comes from BE.
 *
 * The affix match requires a separating space (`"All "`) rather than a bare
 * prefix so unrelated categories that merely start with those letters (e.g.
 * "Allies", "Allocation") aren't misread as the All row.
 */
export function isAllNode(node: PredictionNavNode): boolean {
  return (
    node.slug === ALL_NODE_SLUG ||
    node.display_name === ALL_NODE_DISPLAY_NAME ||
    node.display_name.startsWith(`${ALL_NODE_DISPLAY_NAME} `)
  )
}

/**
 * True when `folder`'s All row should be highlighted for `selectedPath`.
 *
 * "An All context" means the user is viewing the folder's unfiltered union
 * rather than a specific sub-category. From the real nav tree, a sports folder
 * (e.g. Soccer, `/sports/soccer`) carries:
 *   - an All child destination (`/sports/soccer/all`)
 *   - direct `scope` children for the chips (`/sports/soccer/games`,
 *     `/sports/soccer/futures`)
 *   - real sub-categories (`/sports/soccer/epl`) which have their *own* scopes
 *     (`/sports/soccer/epl/games`).
 *
 * The All row lights up when the selection is any of the folder-level All
 * contexts:
 *   1. the folder path itself (`/sports/soccer`)
 *   2. the All child's own path (`/sports/soccer/all`)
 *   3. one of the folder's *direct* scope children (`/sports/soccer/games`)
 *
 * It must NOT light up on a real sub-category (`/sports/soccer/epl`) or that
 * sub-category's own scope — those belong to the sub-category, not the
 * folder's All. We get this for free by only matching the folder's *direct*
 * children's paths.
 *
 * Returns false when the folder has no All child at all, so folders without an
 * All entry never claim a phantom All highlight.
 */
export function isFolderAllActive(folder: PredictionNavNode, selectedPath: string): boolean {
  const children = folder.children ?? []
  const hasAllChild = children.some(isAllNode)
  if (!hasAllChild) return false
  if (folder.path === selectedPath) return true
  return children.some(child => {
    if (!child.path) return false
    if (isAllNode(child) && child.path === selectedPath) return true
    if (child.node_type === 'scope' && child.path === selectedPath) return true
    return false
  })
}

/**
 * The `id` of whichever sibling in `nodes` sits on the selected path, or
 * `null` if none do. Used to seed the per-level accordion's open slot so the
 * folder containing the current selection starts expanded (one open at a
 * time, OG-7173 #3b). Returns the first match by render order — siblings
 * shouldn't overlap on the path, but if a malformed tree nested them, the
 * topmost wins.
 */
export function activeChildId(nodes: PredictionNavNode[], selectedPath: string): string | null {
  return nodes.find(n => isOnActivePath(n, selectedPath))?.id ?? null
}

/** Duration values (in seconds) that use the clock+bolt icon (5 min, 20 min, 2 h). */
const CLOCK_BOLT_DURATIONS = new Set(['300', '1200', '7200'])

/**
 * True when `node` is a 5 min, 20 min, or 2 h duration destination.
 * These use the clock+bolt icon; all other durations use the plain clock icon.
 */
export function isQuickDurationNode(node: PredictionNavNode): boolean {
  return !!node.filter?.duration && CLOCK_BOLT_DURATIONS.has(node.filter.duration)
}
